import React, { useEffect } from "react";
import styled from "styled-components";
import momentTz from "moment-timezone";
import { DateTimePicker } from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { HeaderBar } from "@sonder/ui/source/styled/fragments/HeaderBar";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { useSegment } from "analytics/segment";
import { clearSelectedBooking } from "checkouts/actions";
import { CheckoutModal } from "checkouts/components/CheckoutModal";
import { CheckoutsList } from "checkouts/components/CheckoutsList";
import { CheckoutStatusFilters } from "checkouts/components/Filters/CheckoutStatusFilters";
import { ClearFilterButton } from "checkouts/components/Filters/ClearFilterButton";
import { useCanViewCheckouts } from "checkouts/hooks/useCanViewCheckouts";
import { getCheckoutDateRange } from "checkouts/reducer";
import { useStore } from "checkouts/store";
import { EmptyContent } from "core/components/EmptyContent";
import { Layout } from "core/components/Layout";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { updateDateRange } from "schedule/actions";
import { SameDayCleansFilterButton } from "schedule/components/Filters/SameDayCleansFilterButton";
import {
  TimeRangeOuterContainer,
  createTrigger,
} from "schedule/components/ScheduleView";
import { useStore as useScheduleStore } from "schedule/store";
import { BuildingFilters } from "tasks/components/Filters/BuildingFilters";

const TodayButton = styled(Button)`
  margin-right: 1rem;
`;

const DateTimePickerContainer = styled.div`
  display: flex;
`;

export const CheckoutsViewContent = () => {
  const {
    dispatch,
    state: { booking: selectedBooking },
  } = useStore();
  const { track } = useSegment();
  const timezone = useCurrentUserTimezone();
  const todayDate = momentTz.tz(timezone);
  const todayDateRange = getCheckoutDateRange(todayDate);
  const isDisabled = false;
  const {
    state: { dateRange, isSelecting },
    dispatch: dispatchSchedule,
  } = useScheduleStore();
  useEffect(() => {
    dispatchSchedule(updateDateRange(todayDateRange));
  }, [timezone]);
  return (
    <Layout
      pageSubheader={
        <>
          <HeaderBar>
            <InputGroup>
              <SameDayCleansFilterButton />
              <BuildingFilters />
              <CheckoutStatusFilters />
              <ClearFilterButton />
            </InputGroup>
          </HeaderBar>
          <TimeRangeOuterContainer isCollapsed={false}>
            <TodayButton
              disabled={isSelecting}
              onClick={() => {
                track({
                  eventName: "checkouts_view",
                  action: "click",
                  element: "today_filter",
                  props: {
                    endAt: todayDateRange.endAt.toISOString(),
                    startAt: todayDateRange.startAt.toISOString(),
                  },
                });
                dispatchSchedule(updateDateRange(todayDateRange));
              }}
            >
              Today
            </TodayButton>
            <DateTimePickerContainer>
              <Dropdown trigger={createTrigger(dateRange.startAt, isDisabled)}>
                {({ isOpen }) =>
                  isOpen && (
                    <DropdownContent>
                      <DateTimePicker
                        date={dateRange.startAt}
                        isOutsideRange={day =>
                          day
                            ? day.isAfter(
                                todayDate.clone().add(1, "day").endOf("day"),
                              )
                            : true
                        }
                        showTimePicker={false}
                        onDateChange={nextDate => {
                          if (nextDate) {
                            const nextDateRange =
                              getCheckoutDateRange(nextDate);
                            track({
                              eventName: "checkouts_view",
                              action: "click",
                              element: "date_range_filter",
                              props: {
                                endAt: nextDateRange.endAt.toISOString(),
                                startAt: nextDateRange.startAt.toISOString(),
                              },
                            });
                            dispatchSchedule(updateDateRange(nextDateRange));
                          }
                        }}
                      />
                    </DropdownContent>
                  )
                }
              </Dropdown>
            </DateTimePickerContainer>
          </TimeRangeOuterContainer>
        </>
      }
      pageTitle="Checkouts"
    >
      <CheckoutsList />
      <CheckoutModal
        booking={selectedBooking}
        isOpen={!!selectedBooking?.id}
        onClose={() => dispatch(clearSelectedBooking())}
      />
    </Layout>
  );
};

export const CheckoutsView = () => {
  const { loading, canViewCheckouts } = useCanViewCheckouts();
  if (!canViewCheckouts)
    return (
      <Layout pageSubheader="" pageTitle="Checkouts">
        {!loading && (
          <EmptyContent>
            <p>
              Your account is not currently able to view Checkouts
              <br />
              This feature is currently in early access.
            </p>
          </EmptyContent>
        )}
      </Layout>
    );

  return <CheckoutsViewContent />;
};
